import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Button,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Icon,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Avatar,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Tab,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Image,
  Link,
  Spinner,
  Alert,
  AlertIcon,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  useBreakpointValue,
} from '@chakra-ui/react';
import { SearchIcon, StarIcon, PhoneIcon, EmailIcon, CalendarIcon, HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { 
  FaHeartbeat, 
  FaLungs, 
  FaBrain, 
  FaChild, 
  FaVenus, 
  FaEye, 
  FaBone, 
  FaStethoscope,
  FaHome,
  FaBuilding,
  FaUserMd,
  FaVial,
  FaMapMarkerAlt,
  FaClock,
  FaDollarSign,
  FaMicroscope,
  FaFlask,
  FaShieldAlt,
  FaSearch
} from 'react-icons/fa';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// Medical specialty categories with patient-friendly names and icons
const MEDICAL_SPECIALTIES = [
  {
    id: 'primary_care',
    name: 'General Health',
    icon: FaStethoscope,
    color: 'blue.500',
    description: 'Routine checkups, basic blood work'
  },
  {
    id: 'cardiology',
    name: 'Heart & Cardiovascular',
    icon: FaHeartbeat,
    color: 'red.500',
    description: 'Tests for heart health, cholesterol, blood pressure'
  },
  {
    id: 'endocrinology',
    name: 'Diabetes & Hormones',
    icon: FaStethoscope,
    color: 'purple.500',
    description: 'Blood sugar, thyroid, hormone levels'
  },
  {
    id: 'women_s_health',
    name: 'Women\'s Health',
    icon: FaVenus,
    color: 'pink.500',
    description: 'Pregnancy, reproductive health, mammograms'
  },
  {
    id: 'oncology',
    name: 'Cancer Screening',
    icon: FaShieldAlt,
    color: 'orange.500',
    description: 'Cancer markers, screening tests'
  },
  {
    id: 'infectious_disease',
    name: 'Infections & STDs',
    icon: FaMicroscope,
    color: 'teal.500',
    description: 'STD testing, infections, immunity'
  },
  {
    id: 'nephrology',
    name: 'Kidney Health',
    icon: FaFlask,
    color: 'cyan.500',
    description: 'Kidney function, urine tests'
  },
  {
    id: 'pediatrics',
    name: 'Children\'s Health',
    icon: FaChild,
    color: 'green.500',
    description: 'Tests for babies, kids, and teens'
  },
  {
    id: 'allergy___immunology',
    name: 'Allergies & Immunity',
    icon: FaShieldAlt,
    color: 'red.400',
    description: 'Allergy testing, immune system'
  },
  {
    id: 'gastroenterology',
    name: 'Digestive Health',
    icon: FaStethoscope,
    color: 'yellow.500',
    description: 'Stomach, intestines, liver tests'
  },
  {
    id: 'neurology',
    name: 'Brain & Nervous System',
    icon: FaBrain,
    color: 'indigo.500',
    description: 'Brain health, nervous system disorders'
  },
  {
    id: 'pulmonology',
    name: 'Lung Health',
    icon: FaLungs,
    color: 'blue.400',
    description: 'Breathing tests, lung function'
  }
];

// Function to map test names to medical specialties
const getTestSpecialty = (testName) => {
  const name = testName.toLowerCase();
  
  // Cardiology / Heart Health
  if (name.includes('cholesterol') || name.includes('lipid') || name.includes('cardiac') || 
      name.includes('heart') || name.includes('ecg') || name.includes('troponin')) {
    return 'cardiology';
  }
  
  // Endocrinology / Diabetes & Hormones
  if (name.includes('thyroid') || name.includes('tsh') || name.includes('insulin') || 
      name.includes('glucose') || name.includes('hemoglobin a1c') || name.includes('diabetes') ||
      name.includes('hormone') || name.includes('testosterone') || name.includes('estrogen')) {
    return 'endocrinology';
  }
  
  // Women's Health
  if (name.includes('pregnancy') || name.includes('hcg') || name.includes('pap') || 
      name.includes('mammogram') || name.includes('ovarian') || name.includes('cervical')) {
    return 'womens_health';
  }
  
  // Oncology / Cancer
  if (name.includes('psa') || name.includes('cancer') || name.includes('tumor') || 
      name.includes('oncology') || name.includes('biopsy')) {
    return 'oncology';
  }
  
  // Infectious Disease
  if (name.includes('std') || name.includes('hiv') || name.includes('hepatitis') || 
      name.includes('infection') || name.includes('viral') || name.includes('bacterial')) {
    return 'infectious_disease';
  }
  
  // Kidney Health
  if (name.includes('kidney') || name.includes('creatinine') || name.includes('urea') || 
      name.includes('urine') || name.includes('urinalysis') || name.includes('renal')) {
    return 'nephrology';
  }
  
  // Pediatrics
  if (name.includes('pediatric') || name.includes('child') || name.includes('infant') || 
      name.includes('newborn')) {
    return 'pediatrics';
  }
  
  // Allergy & Immunity
  if (name.includes('allergy') || name.includes('ige') || name.includes('immune') || 
      name.includes('antibody') || name.includes('sensitivity')) {
    return 'allergy';
  }
  
  // Digestive Health
  if (name.includes('liver') || name.includes('hepatic') || name.includes('digestive') || 
      name.includes('stomach') || name.includes('intestinal') || name.includes('colon')) {
    return 'gastroenterology';
  }
  
  // Default to primary care for common tests
  return 'primary_care';
};

// Function to get common reasons for tests
const getCommonReasons = (testName) => {
  const name = testName.toLowerCase();
  
  if (name.includes('cbc') || name.includes('complete blood count')) {
    return ['Routine checkup', 'Anemia screening', 'Infection detection'];
  }
  if (name.includes('cholesterol') || name.includes('lipid')) {
    return ['Heart disease prevention', 'Medication monitoring'];
  }
  if (name.includes('thyroid') || name.includes('tsh')) {
    return ['Fatigue', 'Weight changes', 'Mood changes'];
  }
  if (name.includes('pregnancy') || name.includes('hcg')) {
    return ['Pregnancy confirmation', 'Fertility treatment'];
  }
  if (name.includes('psa')) {
    return ['Prostate cancer screening', 'Men over 50'];
  }
  if (name.includes('std')) {
    return ['STD screening', 'New relationship', 'Symptoms'];
  }
  if (name.includes('vitamin d')) {
    return ['Bone health', 'Fatigue', 'Immune system'];
  }
  if (name.includes('hemoglobin a1c')) {
    return ['Diabetes monitoring', 'Blood sugar control'];
  }
  if (name.includes('testosterone')) {
    return ['Low energy', 'Muscle mass', 'Fertility'];
  }
  if (name.includes('urinalysis')) {
    return ['Kidney function', 'Infection screening', 'Routine checkup'];
  }
  
  return ['Health screening', 'Routine checkup'];
};

// Function to determine if fasting is required
const isFastingRequired = (testName) => {
  const name = testName.toLowerCase();
  return name.includes('cholesterol') || name.includes('lipid') || 
         name.includes('glucose') || name.includes('metabolic panel');
};

// Transform database test data into UI format
const transformTestData = (dbTests) => {
  return dbTests.map(test => ({
    id: test.id,
    name: test.name,
    type: test.type || 'test',
    specialty: test.category ? test.category.toLowerCase().replace(/[^a-z0-9]/g, '_') : getTestSpecialty(test.name),
    description: test.description || `Laboratory ${test.type || 'test'} for ${test.name.toLowerCase()}`,
    price: test.cash_price,
    cash_price: test.cash_price,
    tubeColor: test.tube_top_color || 'Red',
    fastingRequired: test.fasting_required !== undefined ? test.fasting_required : isFastingRequired(test.name),
    fasting_required: test.fasting_required,
    commonFor: test.common_uses ? test.common_uses.split(',').map(use => use.trim()) : getCommonReasons(test.name),
    category: test.category || 'Primary Care'
  }));
};

// Transform database doctor data into UI format
const transformDoctorData = (dbDoctors) => {
  return dbDoctors.map(doctor => ({
    id: doctor.id,
    name: doctor.full_name,
    specialty: doctor.clinic_name || 'Family Medicine',
    rating: doctor.rating || null, // Use actual rating from database
    location: doctor.address || 'Medical Center',
    phone: doctor.phone || null,
    email: doctor.email || null,
    avatar: doctor.avatar_url || null
  }));
};

// Transform database phlebotomist data into phlebotomist format
const transformMobileLabData = (dbPhlebotomists) => {
  return dbPhlebotomists.map(phlebotomist => ({
    id: phlebotomist.id,
    name: phlebotomist.company_name || phlebotomist.full_name,
    rating: phlebotomist.rating || null, // Use actual rating from database
    serviceAreas: phlebotomist.service_areas ? 
      (Array.isArray(phlebotomist.service_areas) ? phlebotomist.service_areas : ['Service Area']) : 
      ['Local Area'],
    homeVisit: true,
    walkin: false,
    price: phlebotomist.min_draw_fee || 45,
    hours: '7 AM - 7 PM',
    phone: phlebotomist.phone || '(555) 345-6789'
  }));
};

// Transform database lab data into processing lab format
const transformProcessingLabData = (dbLabs) => {
  return dbLabs.map(lab => ({
    id: lab.id,
    name: lab.name,
    rating: lab.rating || null, // Use actual rating from database
    location: lab.address || 'Laboratory Location',
    resultTime: '24-48 hours',
    specialties: ['All Tests'],
    phone: lab.contact_phone || '(555) 567-8901'
  }));
};

const TestDirectory = () => {
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredTests, setFilteredTests] = useState([]);
  const [tests, setTests] = useState([]);
  const [labs] = useState([]);
  const [doctors] = useState([]);
  const [phlebotomists] = useState([]);
  const [transformedTests, setTransformedTests] = useState([]);
  const [transformedDoctors] = useState([]);
  const [transformedMobileLabs] = useState([]);
  const [transformedProcessingLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  
  // Mobile menu state
  const { isOpen: isMobileMenuOpen, onOpen: onMobileMenuOpen, onClose: onMobileMenuClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  // Fetch data from database
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch both tests and panels from new structure
      const [testsResult, panelsResult] = await Promise.all([
        supabase.from('tests').select('*').order('name'),
        supabase.from('panels').select('*').order('name')
      ]);

      if (testsResult.error) throw testsResult.error;
      if (panelsResult.error) throw panelsResult.error;

      // Combine tests and panels into a single array
      const combinedData = [
        ...(testsResult.data || []).map(test => ({ ...test, type: 'test' })),
        ...(panelsResult.data || []).map(panel => ({ ...panel, type: 'panel' }))
      ];

      setTests(combinedData);

      // Transform data after fetching
      const transformedTestData = transformTestData(combinedData);

      setTransformedTests(transformedTestData);
      setFilteredTests(transformedTestData);

    } catch (error) {
      console.error('Error fetching test data:', error);
      toast({
        title: 'Error loading tests',
        description: 'Please try again later',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter tests based on search and specialty
  useEffect(() => {
    let filtered = transformedTests;

    if (selectedSpecialty) {
      filtered = filtered.filter(test => test.specialty === selectedSpecialty);
    }

    if (searchTerm) {
      filtered = filtered.filter(test => 
        test.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        test.commonFor.some(condition => 
          condition.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    setFilteredTests(filtered);
  }, [searchTerm, selectedSpecialty, transformedTests]);

  const handleTestSelect = (test) => {
    // Navigate to detailed test page
    navigate(`/test-directory/${test.id}`);
  };

  const handleBookTest = (test) => {
    // Navigate to actual booking form with test pre-selected
    navigate(`/blood-draw?testId=${test.id}&labId=${test.lab_id}`);
  };

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="50vh">
          <Spinner size="xl" />
        </Flex>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header with Navigation */}
      <Flex 
        as="header" 
        align="center" 
        justify="space-between" 
        px={{ base: 4, md: 12 }} 
        py={3} 
        bg="white" 
        boxShadow="sm" 
        position="sticky" 
        top={0} 
        zIndex={10}
      >
        <HStack spacing={3} align="center">
          <Image src="/logo.png" alt="At Home Blood Draw Logo" maxW="320px" w="100%" />
        </HStack>
        
        {isMobile ? (
          <>
            <IconButton 
              icon={<HamburgerIcon />} 
              variant="ghost" 
              onClick={onMobileMenuOpen} 
              aria-label="Open menu" 
              fontSize="2xl" 
            />
            <Drawer isOpen={isMobileMenuOpen} placement="right" onClose={onMobileMenuClose}>
              <DrawerOverlay />
              <DrawerContent>
                <DrawerHeader display="flex" alignItems="center" justifyContent="space-between">
                  <Box>Menu</Box>
                  <IconButton 
                    icon={<CloseIcon />} 
                    onClick={onMobileMenuClose} 
                    aria-label="Close menu" 
                  />
                </DrawerHeader>
                <DrawerBody>
                  <VStack spacing={4} align="stretch" mt={4}>
                    <Button as={RouterLink} to="/test-directory" colorScheme="green" variant="solid" size="lg">
                      Test Directory
                    </Button>
                    <Button as={RouterLink} to="/lab-directory" colorScheme="purple" variant="ghost" size="lg">
                      Lab Directory
                    </Button>
                    <Button as={RouterLink} to="/doctor/login" colorScheme="blue" variant="ghost" size="lg">
                      Physician Login
                    </Button>
                    <Button as={RouterLink} to="/patient/login" colorScheme="blue" variant="ghost" size="lg">
                      Patient Login
                    </Button>
                  </VStack>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </>
        ) : (
          <HStack spacing={4}>
            <Button as={RouterLink} to="/test-directory" colorScheme="green" variant="solid">
              Test Directory
            </Button>
            <Button as={RouterLink} to="/lab-directory" colorScheme="purple" variant="ghost">
              Lab Directory
            </Button>
            <Button as={RouterLink} to="/doctor/login" colorScheme="blue" variant="ghost">
              Physician Login
            </Button>
            <Button as={RouterLink} to="/patient/login" colorScheme="blue" variant="ghost">
              Patient Login
            </Button>
          </HStack>
        )}
      </Flex>

      <Container maxW="6xl" py={8}>
        {/* Page Title */}
        <Heading size="xl" mb={6} color="gray.700" fontWeight="normal">
              Test Directory
            </Heading>

        {/* Search Section */}
        <VStack spacing={4} align="stretch" mb={8}>
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            Search for Tests, Diseases, or Conditions
            </Text>

          <HStack spacing={0}>
            <InputGroup flex="1">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Chloride"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
                border="1px solid"
                borderColor="gray.300"
                borderRightRadius="0"
                fontSize="sm"
                h="10"
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "none"
                }}
              />
            </InputGroup>
            
            <Button
              colorScheme="blue"
              borderLeftRadius="0"
              h="10"
              px={6}
              fontSize="sm"
              fontWeight="bold"
            >
              SEARCH
            </Button>
          </HStack>

          <HStack spacing={4}>
            <HStack spacing={2}>
              <Icon as={SearchIcon} color="gray.400" boxSize={4} />
              <Text fontSize="xs" color="gray.500">SEARCH STRENGTH</Text>
              <Button variant="ghost" size="xs" color="blue.600">
                ∨
              </Button>
            </HStack>
            
            <HStack spacing={2} fontSize="xs" color="gray.500">
              <Text>See also</Text>
              <Text color="blue.600" textDecoration="underline" cursor="pointer">
                ({filteredTests.length}) TEST GUIDES
              </Text>
              <Text color="blue.600" textDecoration="underline" cursor="pointer">
                (1) ALGORITHMS
            </Text>
            </HStack>
          </HStack>
        </VStack>

        {/* Results - Quest Style */}
          <Box>
          <HStack justify="space-between" align="center" mb={4}>
            <Text fontSize="sm" color="gray.600">
              <Text as="span" fontWeight="bold">{filteredTests.length} results</Text> with <Text as="span" fontWeight="bold">Low</Text> strength
            </Text>
            <HStack spacing={4} fontSize="xs">
              <Text color="blue.600" cursor="pointer">ENABLE TEST COMPARE MODE</Text>
              <HStack spacing={2}>
                <Text color="gray.500">Sort:</Text>
                <Text color="gray.700">Closest Match</Text>
                <Text color="blue.600">∨</Text>
              </HStack>
            </HStack>
          </HStack>

            {filteredTests.length === 0 ? (
            <Alert 
              status="info" 
              borderRadius="md"
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              py={4}
            >
              <AlertIcon color="blue.500" />
              <Box>
                <Text color="blue.800">
                No tests found matching your criteria. Try a different search or category.
                </Text>
              </Box>
              </Alert>
            ) : (
            <VStack spacing={0} align="stretch">
              {filteredTests.map((test, index) => (
                <Box
                    key={test.id}
                  borderBottom="1px solid"
                  borderColor="gray.200"
                  py={4}
                  _hover={{ bg: "gray.50" }}
                    cursor="pointer"
                    onClick={() => handleTestSelect(test)}
                >
                  <HStack align="start" spacing={4}>
                    {/* Test Code */}
                    <VStack spacing={1} minW="80px">
                      <Text fontSize="xs" color="gray.500" fontWeight="medium">
                        Test Code
                      </Text>
                      <Text fontSize="sm" fontWeight="bold" color="gray.700">
                        {test.code || (test.type === 'panel' ? 'PNL' : 'TST') + (100 + index)}
                          </Text>
        </VStack>

                  {/* Test Details */}
                    <VStack align="start" spacing={2} flex="1">
                      <HStack spacing={2}>
                        <Heading size="md" color="blue.600" fontWeight="semibold">
                          {test.name}
                        </Heading>
                        <Badge 
                          colorScheme={test.type === 'panel' ? 'purple' : 'blue'}
                          fontSize="xs"
                          variant="subtle"
                        >
                          {test.type === 'panel' ? 'Panel' : 'Test'}
                        </Badge>
                      </HStack>
                      
                      <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                        {test.description || `Clinical laboratory ${test.type || 'test'} for ${test.name.toLowerCase()}`}
                        {test.description && test.description.length > 200 && (
                          <Text as="span" color="blue.600" ml={1} cursor="pointer">
                            ...
                          </Text>
                        )}
                      </Text>

                                            <VStack align="start" spacing={2} fontSize="xs" color="gray.500">
                         <HStack spacing={4} wrap="wrap">
                           {test.fasting_required && (
                             <Text color="orange.600" fontWeight="medium">
                               🍽️ Fasting Required
                             </Text>
                           )}
                           {test.cash_price && (
                             <Text>
                               💰 Price: <Text as="span" color="blue.600" fontWeight="bold">${test.cash_price}</Text>
                             </Text>
                           )}
                           {test.preferred_specimen && (
                             <Text>
                               🧪 Specimen: {test.preferred_specimen}
                             </Text>
                           )}
                           {test.minimum_volume && (
                             <Text>
                               📏 Min Volume: {test.minimum_volume}
                             </Text>
                           )}
                                    </HStack>

                         <HStack spacing={4} wrap="wrap">
                           {test.tube_top_color && (
                             <Text>
                               🟢 Tube: {test.tube_top_color}
                             </Text>
                           )}
                           {test.methodology && (
                             <Text>
                               🔬 Method: {test.methodology}
                             </Text>
                           )}
                           {test.turnaround_time && (
                             <Text>
                               ⏱️ TAT: {test.turnaround_time}
                                    </Text>
                           )}
                           {test.reference_range && (
                             <Text>
                               📊 Range: {test.reference_range.substring(0, 50)}...
                             </Text>
                           )}
                                    </HStack>

                         {test.clinical_significance && (
                           <Text color="gray.600" fontSize="xs" noOfLines={2}>
                             <Text as="span" fontWeight="medium">Clinical:</Text> {test.clinical_significance}
                                    </Text>
                          )}
                        </VStack>
                    </VStack>

                    {/* Action Menu */}
                    <VStack spacing={1}>
                      <Text color="gray.400" fontSize="lg" cursor="pointer" _hover={{ color: "gray.600" }}>
                        ⋮
                                    </Text>
                                  </VStack>
                                </HStack>
                </Box>
              ))}
                </VStack>
              )}
        </Box>


      </Container>
    </Box>
  );
};

export default TestDirectory; 